package nl.appstuff.padelchecker.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.components.BrandLogo
import nl.appstuff.padelchecker.site.directory.Directory
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import org.jetbrains.compose.web.css.*

@Composable
fun FooterSection() {
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(SiteColors.backgroundVariantDark)
            .id("footer"),
        contentAlignment = Alignment.Center,
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 36.px, leftRight = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                if (isDesktop) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .maxWidth(1120.px),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Top,
                    ) {
                        FooterBrand()
                        FooterLinks()
                    }
                } else {
                    Column(
                        modifier = Modifier.fillMaxWidth().maxWidth(560.px),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(32.px),
                    ) {
                        FooterBrand()
                        FooterLinks(centered = true)
                    }
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .background(SiteColors.whiteDivider),
            )

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 18.px, leftRight = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                val strings = LocalSiteStrings.current
                Row {
                    SpanText(
                        strings.footerRightsReserved(2026),
                        modifier = Modifier
                            .fontSize(16.px)
                            .color(SiteColors.whiteDisabled),
                    )
                    Link(
                        path = "/contact",
                        variant = UncoloredLinkVariant,
                        modifier = Modifier
                            .padding(left = 10.px)
                    ) {
                        SpanText(
                            "- contact@padelchecker.nl",
                            modifier = Modifier
                                .fontSize(16.px)
                                .color(SiteColors.whiteDisabled),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FooterBrand() {
    BrandLogo(
        lightVariant = true,
        modifier = Modifier.width(148.px),
    )
}

@Composable
private fun FooterLinks(centered: Boolean = false) {
    val strings = LocalSiteStrings.current
    Row(
        horizontalArrangement = if (centered) Arrangement.spacedBy(30.px) else Arrangement.spacedBy(70.px),
        verticalAlignment = Alignment.Top,
    ) {
        FooterLinkGroup(
            title = strings.footerAbout,
            links = listOf(
                strings.navFindCourt to "/zoeken",
                strings.directoryPadelIndex to "/padel",
                strings.navFaq to "#faq",
            ),
        )
        FooterLinkGroup(
            title = strings.navDownload,
            links = listOf(
                "App Store (iOS)" to "https://apps.apple.com/nl/app/padelchecker-nl-padelbanen/id6759914204",
                "Play Store (Android)" to "https://play.google.com/store/apps/details?id=nl.appstuff.padelchecker",
            ),
        )
        FooterLinkGroup(
            title = strings.footerContact,
            links = listOf(
                strings.footerPrivacy to "/privacy",
                strings.footerContact to "/contact",
            ),
        )
        if (Directory.provinces.isNotEmpty()) {
            FooterLinkGroup(
                title = strings.directoryPadelIndex,
                links = Directory.provinces.sortedBy { it.name }.map {
                    it.name to "/padel-in/${it.slug}"
                },
            )
        }
    }
}

@Composable
private fun FooterLinkGroup(title: String, links: List<Pair<String, String>>) {
    Column(verticalArrangement = Arrangement.spacedBy(10.px)) {
        SpanText(
            title.uppercase(),
            modifier = Modifier
                .fontSize(16.px)
                .fontWeight(FontWeight.SemiBold)
                .color(SiteColors.whiteDisabled)
                .styleModifier { property("letter-spacing", "0.4px") },
        )
        links.forEach { (label, path) ->
            Link(
                path = path,
                variant = UncoloredLinkVariant,
                modifier = Modifier
                    .color(SiteColors.whiteSecondary)
                    .fontSize(16.px)
                    .textDecorationLine(TextDecorationLine.None),
            ) {
                SpanText(label)
            }
        }
    }
}
